<template>
  <div>
    <v-row no-gutters>
      <span class="grey--text text-darken-1 text-body-2 text-center pb-2">
        {{ $t('form.committees.instruction') }}
      </span>
      <v-form ref="form" v-model="formValid" @submit.prevent="" class="flex-grow-1">
        <v-row>
          <v-col :cols="12" :sm="6">
            <platform-select-field
              v-model="committeeType"
              clearable
              :label="$t('form.committees.committeeType')"
              :items="committeeTypeOptions"
            />
          </v-col>
          <v-col :cols="12" :sm="6">
            <v-form-base
              :model="committee"
              :schema="formSchema"
              @input="handleInput"
            >
              <template #slot-top-key-form-base-slot-tags="{ obj, id, index }">
                <pre>{{ { obj, id, index } }}</pre>
              </template>
            </v-form-base>
          </v-col>
        </v-row>
      </v-form>
    </v-row>
    <v-row no-gutters>
      <p>{{ $t('form.committees.individualsTitle') }}</p>
    </v-row>

    <individuals-selector
      v-if="!hideMemberSelector"
      v-model="selectedIndividualIds"
      :company-individuals="companyIndividuals"
      :loading="fetchingIndividuals"
      :saving="saving"
      class="mt-3"
    />

    <!-- Pending members from import preview only (not import results completion) -->
    <div v-if="!importCompletionMode && displayedMembers && displayedMembers.length > 0" class="mt-3 mb-4">
      <p class="text-body-2 grey--text text--darken-1 mb-2">
        {{ $t('form.committees.pendingMembersLabel') }}
      </p>
      <v-chip-group
        v-model="selectedPendingIndices"
        multiple
        column
      >
        <v-chip
          v-for="(name, index) in displayedMembers"
          :key="index"
          :value="index"
          filter
          outlined
          small
        >
          {{ name }}
        </v-chip>
      </v-chip-group>
    </div>
  </div>
</template>

<script lang="ts">
import { Company } from '@/API'
import IndividualsSelector from '@/components/individuals/IndividualsSelector.vue'
import Vue, { PropType } from 'vue'
import VFormBase from 'vuetify-form-base'
import { findIndividualByName } from '@betterboards/shared/helpers/entities/Individual/findIndividualByName'
import { getPreferredName } from '@betterboards/shared/helpers/entities/Individual/getPreferredName'
import { SelectItem } from '@/helpers/forms'
import { CommitteeTag } from '@betterboards/shared/types/API'
import PlatformSelectField from '@/components/shared/inputs/PlatformSelectField.vue'
import { PlatformIndividual } from '@betterboards/shared/types/Individual'
import { PlatformCommittee, PlatformCommitteeIndividual } from '@betterboards/shared/types/Committee'

export default Vue.extend({
  name: 'CommitteeForm',
  components: {
    PlatformSelectField,
    IndividualsSelector,
    VFormBase
  },
  props: {
    value: { type: Object as PropType<PlatformCommittee>, required: true },
    saving: { type: Boolean, required: false },
    showTypeOption: { type: Boolean, default: false },
    pendingMembers: { type: Array as PropType<string[]>, default: () => [] },
    allAvailableMembers: { type: Array as PropType<string[]>, default: () => [] },
    hideMemberSelector: { type: Boolean, default: false },
    importCompletionMode: { type: Boolean, default: false },
    importMemberNames: { type: Array as PropType<string[]>, default: () => [] },
    matchCandidates: { type: Array as PropType<PlatformIndividual[]>, default: () => [] }
  },
  data: () => ({
    fetchingIndividuals: false,
    companyIndividuals: <PlatformIndividual[]>[],
    selectedIndividualIds: <string[]>[],
    selectedPendingIndices: <number[]>[],
    isInitializingSelection: false,
    formValid: false
  }),
  watch: {
    isValid (val) {
      this.$emit('valid', val)
    },
    displayedMembers: {
      immediate: true,
      handler (): void {
        if (!this.importCompletionMode) {
          this.initSelectedMembers()
        }
      }
    },
    pendingMembers: {
      immediate: true,
      handler (): void {
        if (!this.importCompletionMode) {
          this.initSelectedMembers()
        }
      }
    },
    selectedPendingMembers (members: string[]): void {
      if (!this.isInitializingSelection) {
        this.$emit('pendingMembersChanged', members)
      }
    },
    selectedIndividualIds (selectedIndividualIds): void {
      if (!this.committee.individuals) {
        this.committee.individuals = { items: [] }
      }
      this.committee.individuals.items = this.companyIndividuals
        .filter(
          (i) => selectedIndividualIds.includes(i.id)
        )
        .map((individual: PlatformIndividual): PlatformCommitteeIndividual => {
          return {
            committeeId: this.committee.id,
            companyId: this.committee.companyId,
            individualId: individual.id,
            createdAt: individual.createdAt, // Not technically correct and a bit hacky but works for now
            individual
          }
        })
    }
  },
  mounted (): void {
    this.init()
  },
  computed: {
    selectedCompany (): Company | undefined {
      return this.$store.state.Company.selectedCompany
    },
    committee: {
      get (): PlatformCommittee {
        return this.value
      },
      set (committee: PlatformCommittee) {
        this.$emit('input', committee)
      }
    },
    formSchema (): any {
      return {
        name: {
          type: 'BBTextField',
          label: this.$t('form.committees.committeeName'),
          col: { xs: 12 },
          required: true,
          disabled: this.saving
        }
      }
    },
    isValid (): boolean {
      return this.formValid
    },
    matchedIndividuals (): Map<string, PlatformIndividual> {
      const matches = new Map<string, PlatformIndividual>()
      const allMembers = this.allAvailableMembers?.length > 0
        ? this.allAvailableMembers
        : (this.pendingMembers || [])

      for (const memberName of allMembers) {
        const normalizedName = memberName.toLowerCase().trim()
        const match = this.companyIndividuals.find(ind => {
          const preferredName = this.getPreferredName(ind)?.toLowerCase().trim()
          const fullName = `${ind.firstName || ''} ${ind.familyName || ''}`.toLowerCase().trim()
          return preferredName === normalizedName || fullName === normalizedName
        })
        if (match) {
          matches.set(memberName, match)
        }
      }
      return matches
    },
    displayedMembers (): string[] {
      const allMembers = this.allAvailableMembers?.length > 0
        ? this.allAvailableMembers
        : (this.pendingMembers || [])

      // Filter out members that match existing individuals
      return allMembers.filter(name => !this.matchedIndividuals.has(name))
    },
    selectedPendingMembers (): string[] {
      const members = this.displayedMembers
      if (!members || members.length === 0) {
        return []
      }
      return this.selectedPendingIndices
        .filter(index => index < members.length)
        .map(index => members[index])
    },
    individualsList (): PlatformIndividual[] {
      return this.$store.getters['Individual/individualsList']
    },
    committeeTypeOptions (): SelectItem[] {
      const options: SelectItem[] = [
        ...Object.values(CommitteeTag).map((tag: CommitteeTag): SelectItem => ({
          text: this.$t(`global.committeeTypeNames.${tag as string}`) as string,
          value: tag
        }))
      ]
      return options
    },
    committeeType: {
      get (): CommitteeTag | undefined {
        return this.committee.tags?.[0]
      },
      set (committeeType: CommitteeTag): void {
        const oldCommitteeType: CommitteeTag | undefined = this.committeeType
        const oldCommitteeName = this.$t(`global.committeeTypeNames.${oldCommitteeType as string}`)
        if (!this.committee.tags) {
          this.committee.tags = []
        }
        this.committee.tags.splice(0, this.committee.tags.length)
        if (committeeType && !this.committee.tags.includes(committeeType)) {
          this.committee.tags.push(committeeType)
        }
        const translationKey: string = `global.committeeTypeNames.${committeeType as string}`
        if (!this.$te(translationKey)) {
          return
        }
        if (this.committee.name && this.committee.name !== oldCommitteeName) {
          return
        }
        this.committee.name = this.$t(translationKey) as string
      }
    }
  },
  methods: {
    getPreferredName,
    validate () {
      const form: any = this.$refs.form
      form.validate()
    },
    handleInput (input): void {
      this.committee = input.data
    },
    initSelectedMembers (): void {
      this.isInitializingSelection = true

      const displayed = this.displayedMembers
      const current = this.pendingMembers || []

      if (!displayed || displayed.length === 0) {
        this.selectedPendingIndices = []
        this.isInitializingSelection = false
        return
      }

      // Pre-select only the members that belong to the current committee
      this.selectedPendingIndices = displayed
        .map((name, index) => current.includes(name) ? index : -1)
        .filter(index => index !== -1)

      this.$nextTick(() => {
        this.isInitializingSelection = false
      })
    },
    init (): void {
      const selectedCompany = this.selectedCompany
      if (!selectedCompany) {
        return
      }
      this.companyIndividuals = this.$store.getters['Individual/individualsList']
        .map((companyIndividual) => ({
          ...companyIndividual,
          individualId: companyIndividual.individualId ?? companyIndividual.id,
          companyId: companyIndividual.companyId ?? selectedCompany.id
        }))

      // Pre-select individuals already in the committee
      if (this.committee.individuals?.items) {
        this.committee.individuals.items.forEach((individual) => {
          if (this.selectedIndividualIds.includes(individual.individualId)) {
            return
          }
          this.selectedIndividualIds.push(individual.individualId)
        })
      }

      if (this.importCompletionMode) {
        this.preselectImportCompletionMembers()
      } else {
        // Auto-select existing individuals that match pending member names
        this.autoSelectMatchedIndividuals()
      }
    },
    mergeMatchCandidates (individuals: PlatformIndividual[]): PlatformIndividual[] {
      const byId = new Map<string, PlatformIndividual>()
      for (const individual of individuals) {
        if (individual.id) {
          byId.set(individual.id, individual)
        }
      }
      for (const candidate of this.matchCandidates) {
        if (candidate.id && !byId.has(candidate.id)) {
          byId.set(candidate.id, candidate)
        }
      }
      return Array.from(byId.values())
    },
    preselectImportCompletionMembers (): void {
      const matchPool = this.mergeMatchCandidates(this.companyIndividuals)

      for (const memberName of this.importMemberNames) {
        const matchedIndividual = findIndividualByName(memberName, matchPool)
        if (matchedIndividual && !this.selectedIndividualIds.includes(matchedIndividual.id)) {
          this.selectedIndividualIds.push(matchedIndividual.id)
        }
      }
    },
    autoSelectMatchedIndividuals (): void {
      const currentMembers = this.pendingMembers || []

      for (const memberName of currentMembers) {
        const matchedIndividual = this.matchedIndividuals.get(memberName)
        if (matchedIndividual && !this.selectedIndividualIds.includes(matchedIndividual.id)) {
          this.selectedIndividualIds.push(matchedIndividual.id)
        }
      }
    }
  }
})
</script>
